<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { wordsPath } from '@/utils/quran/paths';

/**
 * Ready-made word counts and comparisons — the one-tap way into the word
 * statistics. Each example is a full query: word, language, mode, compare.
 */
const { t, locale } = useI18n();

// Words with many endings go by their stem. German, Dutch and French pairs come from their translations' vocabulary.
const PAIRS = {
  bs: [['džennet · džehennem', 'džennet', 'džehennem'], ['melek · šejtan', 'melek', 'šejtan'], ['strpljenje', 'strplj'], ['milost', 'milost']],
  en: [['world · hereafter', 'world', 'hereafter', 'word'], ['angel · satan', 'angel', 'satan'], ['patience', 'patien'], ['mercy', 'merc']],
  de: [['Paradies · Hölle', 'paradies', 'hölle'], ['Engel · Satan', 'engel', 'satan'], ['Geduld', 'geduld'], ['Barmherzigkeit', 'barmherz']],
  nl: [['paradijs · hel', 'paradijs', 'hel', 'word'], ['engel · satan', 'engel', 'satan'], ['geduld', 'geduld'], ['genade', 'genade']],
  fr: [['paradis · enfer', 'paradis', 'enfer'], ['ange · diable', 'ange', 'diable'], ['patience', 'patien'], ['miséricorde', 'miséricord']],
};

const examples = computed(() => {
  const lang = locale.value;
  const [pair, names, single, another] = PAIRS[lang].map(([label, q, second, mode = 'prefix']) => ({
    label,
    query: { q, vs: second, lang, mode },
  }));
  const list = [
    { label: 'الله', query: { q: 'الله', mode: 'contains' }, arabic: true },
    { label: 'الدنيا · الآخرة', query: { q: 'الدنيا', vs: 'الآخرة', mode: 'contains' }, arabic: true },
    pair,
    { label: 'يوم · شهر', query: { q: 'يوم', vs: 'شهر', mode: 'contains' }, arabic: true },
    names,
    single,
    another,
  ];
  return list.map(({ query: { q, ...rest }, ...example }) => ({ ...example, to: wordsPath(q, rest) }));
});
</script>

<template>
  <ul class="flex flex-wrap gap-2" :aria-label="t('quran.words.examples')">
    <li v-for="example in examples" :key="example.label">
      <router-link
        :to="example.to"
        :class="[
          'inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl bg-white border border-deen-ink/10 text-deen-ink/80 hover:border-deen-ink/30 hover:shadow-sm transition-all',
          example.arabic ? 'text-lg' : 'text-sm font-semibold',
        ]"
      >
        <iconify-icon icon="solar:chart-square-linear" class="text-base text-deen-ink/40" aria-hidden="true" />
        <span :dir="example.arabic ? 'rtl' : undefined" :class="example.arabic && 'arabic-inline'">{{ example.label }}</span>
      </router-link>
    </li>
  </ul>
</template>
